import { isStudioDesktop } from '@repo/studio-desktop-bridge/runtime'
import { useIsStudioAIUserQuery } from '@repo/studio-hooks-studio-ai/use-is-studio-ai-user'
import { useIsTrialUserQuery } from '@repo/studio-hooks-auth/use-is-trial-user-query'
import { useCoinSubscriptionTier } from '@repo/studio-hooks-coins/hooks/use-coin-subscription-tier'
import { useCoinBalance } from '@repo/studio-hooks-coins/use-coin-balance'
import { useCookieValue } from 'hooks/use-cookie-value'
import { COIN_SUBSCRIPTION_BANNER_DISMISSED_COOKIE_NAME } from 'constants/cookies'

const COIN_THRESHOLD = 10_000

/**
 * Drives the STU-7650 top-banner that pitches the released coinPack700k subscription. It is shown
 * users (not in trial) who are low on coins and don't already have any coin subscription, and
 * is dismissible via {@link COIN_SUBSCRIPTION_BANNER_DISMISSED_COOKIE_NAME}. Never shows in
 * Studio Desktop.
 *
 * In `StudioBanners` this banner takes the top slot when both this and the generic 1M
 * low-coins top-up banner would otherwise apply; once dismissed we fall through to the
 * 1M top-up banner so users still have a path to add coins.
 */
export function useCoinSubscriptionBannerQuery() {
  const { tier, subscriptionPlan, hasCoinSubscription, priceCents, isCoinSubscriptionPending } =
    useCoinSubscriptionTier()

  const isStudioAIUserQuery = useIsStudioAIUserQuery()
  const isTrialUserQuery = useIsTrialUserQuery()
  const { data: coinBalance, isPending: isCoinBalancePending } = useCoinBalance()
  const [dismissed] = useCookieValue(COIN_SUBSCRIPTION_BANNER_DISMISSED_COOKIE_NAME)

  const isPending =
    isStudioAIUserQuery.isPending ||
    isTrialUserQuery.isPending ||
    isCoinSubscriptionPending ||
    isCoinBalancePending

  const isStudioAIUser = isStudioAIUserQuery.data === true
  const isTrialUser = isTrialUserQuery.data === true
  const isDismissed = dismissed !== undefined
  const isLowOnCoins = coinBalance !== undefined && coinBalance < COIN_THRESHOLD

  // Gate on `!hasCoinSubscription` (any coin subscription), not `!hasTargetPlan` (the specific
  // released plan). Otherwise a user already on, e.g., CoinPackMonthly1M would get pitched
  // coinPack700k.
  const shouldShow =
    isStudioAIUser &&
    !isTrialUser &&
    !hasCoinSubscription &&
    !isDismissed &&
    isLowOnCoins &&
    !isStudioDesktop()

  return { isPending, data: shouldShow, tier, subscriptionPlan, priceCents, isDismissed }
}
